import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, Alert, Platform, Dimensions, TouchableOpacity } from 'react-native';
import { useUser } from '../../../../context/UserContext';
import Storage from '../../../../utils/Storage';
import FeedbackModal from '../../../Common/FeedbackModal';
import { BACKEND_URL } from '../../../../Config/Const';

const DatosUsuario = forwardRef(({ setEditMode, onSaveChanges }, ref) => {
  // Detectar tamaño de pantalla para responsividad
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 768
  );
  const isMobile = windowWidth < 768;
  useImperativeHandle(ref, () => ({
    handleModificar
  }));

  const {codUsu,
    nombreUsuario,
    mail,
    genero,
    fecnac,
    updateUser
  } = useUser();

  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  
  // Estados para el modal de feedback
  const [feedbackModal, setFeedbackModal] = useState({
    visible: false,
    type: 'success',
    title: '',
    message: ''
  });

  const decodeEntities = (str = '') =>
    String(str)
      .replace(/&ntilde;/g, 'ñ')
      .replace(/&Ntilde;/g, 'Ñ')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');

  const formatDateToAPI = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  // Convierte YYYY-MM-DD a DD/MM/YYYY
  const formatISOToDMY = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    if (iso.includes('/')) return iso; // ya está D/M/Y
    const [y, m, d] = iso.split('-');
    if (y && m && d) return `${d}/${m}/${y}`;
    return iso;
  };

  const validateDateFormat = (dateString) => {
    if (!dateString) return false;
    // Aceptar DD/MM/YYYY o DD-MM-YYYY
    const regexDMY = /^(0?[1-9]|[12][0-9]|3[01])[\/-](0?[1-9]|1[012])[\/-](19|20)\d\d$/;
    // Aceptar también YYYY-MM-DD (por si ya viene así)
    const regexISO = /^(19|20)\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    return regexDMY.test(dateString) || regexISO.test(dateString);
  };

  // Listener para cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    if (nombreUsuario) {
      const [nombrePart, apellidoPart] = nombreUsuario.split(',').map(part => part.trim());
      // nombreUsuario viene como "Apellido, Nombre" desde API
      setApellido(nombrePart || '');
      setNombre(apellidoPart || '');
    }
  }, [nombreUsuario]);

  const [formData, setFormData] = useState({
    mail2: mail || '',
    nombre: nombre || '',
    apellido: apellido || '',
    sex: genero || '',
    fechaNac: fecnac || '',
  });

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      nombre: nombre,
      apellido: apellido,
      mail2: mail || '',
      sex: genero,
      fechaNac: fecnac || ''
    }));
  }, [nombre, apellido, mail, genero, fecnac]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Máscara en vivo para fecha: DD/MM/YYYY (no bloquea escritura)
  const handleDateInput = (raw) => {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 8);
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    let formatted = dd;
    if (digits.length > 2) formatted += `/${mm}`;
    if (digits.length > 4) formatted += `/${yyyy}`;
    setFormData(prev => ({ ...prev, fechaNac: formatted }));
  };

  const handleDateBlur = () => {
    const value = formData.fechaNac || '';
    if (!value) return;
    if (!validateDateFormat(value)) {
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Error',
        message: 'El formato de fecha debe ser DD/MM/YYYY'
      });
    }
  };

  const isValidEmail = (value) => {
    const v = String(value || '').trim();
    if (!v) return false;
    return /^[^\s@]+@[^\s@]+/.test(v);
  };

  const getMissingRequiredFields = () => {
    const missing = [];
    if (!String(formData.nombre || '').trim()) missing.push('Nombre');
    if (!String(formData.apellido || '').trim()) missing.push('Apellido');
    if (!isValidEmail(formData.mail2)) missing.push('Mail');
    if (!String(formData.sex || '').trim()) missing.push('Género');
    const rawFecha = String(formData.fechaNac || '').trim();
    if (!rawFecha) missing.push('Fecha de nacimiento');
    else if (!validateDateFormat(rawFecha)) missing.push('Fecha de nacimiento');
    return missing;
  };

  const isFormValid = getMissingRequiredFields().length === 0;

  const handleModificar = async () => {
    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Faltan datos',
        message: `Completá los siguientes campos: ${missing.join(', ')}`
      });
      return;
    }

    // Convertir a YYYY-MM-DD para el backend (ej: 1994-03-06)
    const raw = (formData.fechaNac || '').trim();
    let fecnacISO = '';

    // Si ya viene en ISO, usarlo tal cual
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) {
      fecnacISO = raw;
    } else {
      // Aceptar dd/mm/yyyy o dd-mm-yyyy
      const dmy = raw.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
      if (dmy) {
        const [_, dd, mm, yyyy] = dmy;
        fecnacISO = `${dd}/${mm}/${yyyy}`;
       // fecnacISO = `${yyyy}/${mm}/${dd}`;
      } else {
        // Aceptar 8 dígitos (ddmmyyyy) sin separadores
        const digits = raw.match(/^(\d{2})(\d{2})(\d{4})$/);
        if (digits) {
          const [, dd, mm, yyyy] = digits;
          fecnacISO = `${dd}/${mm}/${yyyy}`;
         // fecnacISO = `${yyyy}/${mm}/${dd}`;
        }
      }
    }
    // Fallback: intentar parsear solo por dígitos (corrige pérdida de 0 a la izquierda en web)
    if (!fecnacISO && raw) {
      const digitsOnly = raw.replace(/\D/g, '');
      if (digitsOnly.length >= 6 && digitsOnly.length <= 8) {
        // Normalizar a 8 dígitos (ddmmyyyy)
        const padded = digitsOnly.padStart(8, '0');
        const dd = padded.slice(0, 2);
        const mm = padded.slice(2, 4);
        const yyyy = padded.slice(4, 8);
        fecnacISO = `${dd}/${mm}/${yyyy}`;
        //fecnacISO = `${yyyy}/${mm}/${dd}`;


      }
    }

    // Si no se pudo parsear, mostrar error y no enviar
    if (raw && !fecnacISO) {
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Fecha inválida',
        message: 'Usá formato DD/MM/YYYY, DD-MM-YYYY o YYYY-MM-DD.'
      });
      return;
    }
    try {
      const body = JSON.stringify({
        "request": {
          codUsu: codUsu,
          mail: mail,
          mail2: formData.mail2,
          nombre: formData.nombre,
          apellido: formData.apellido,
          sex: formData.sex,
          fecnac: fecnacISO,
        }
      });

      const response = await fetch(`${BACKEND_URL}/rest/usuarios/usuario`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body
      });

      const contentType = response.headers.get('content-type') || '';
      let data = null;
      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        try { data = JSON.parse(text); } catch { data = null; }
      }

      if (!response.ok) {
        // Normalizar estructura para extraer mensaje de error
        const root = data?.response?.dsResultado?.dsResultado
          || data?.response?.dsResultado
          || data?.dsResultado?.dsResultado
          || data?.dsResultado
          || {};
        const apiMsg = decodeEntities(
          root?.resultado?.[0]?.mensaje || ''
        );
        setFeedbackModal({
          visible: true,
          type: 'error',
          title: 'Error',
          message: apiMsg || 'No se pudieron actualizar los datos'
        });
        return;
      }

      // Normalizar estructura (la API puede envolver en 'response' y duplicar 'dsResultado')
      const root = data?.response?.dsResultado?.dsResultado
        || data?.response?.dsResultado
        || data?.dsResultado?.dsResultado
        || data?.dsResultado
        || {};

      // Verificar resultado lógico
      const logicalOk = !!(root?.resultado?.[0]?.resultado);
      if (!logicalOk) {
        const apiMsg = decodeEntities(
          root?.resultado?.[0]?.mensaje || ''
        );
        setFeedbackModal({
          visible: true,
          type: 'error',
          title: 'Error',
          message: apiMsg || 'No se pudieron actualizar los datos'
        });
        return;
      }

      if (root?.Usuario?.[0]) {
        const updatedUser = root.Usuario[0];

        const codUsuResp = updatedUser?.codUsu ? String(updatedUser.codUsu) : (codUsu ? String(codUsu) : '');
        // Mantener formato consistente: "Apellido, Nombre" (como devuelve API)
        const nombreResp = `${(formData.apellido || '').trim()}, ${(formData.nombre || '').trim()}`;
        const mailResp = updatedUser?.mail || mail || '';
        const generoResp = updatedUser?.genero || formData.sex || '';
        const fechaNacResp = updatedUser?.fechaNac || '';
        const fechaNacDMY = formatISOToDMY(fechaNacResp);

        // Guardar en AsyncStorage/localStorage
        try {
          await Storage.setItem('codUsu', codUsuResp);
          await Storage.setItem('nombre', nombreResp);
          await Storage.setItem('mail', mailResp);
          await Storage.setItem('genero', generoResp);
          if (fechaNacDMY) await Storage.setItem('fecnac', fechaNacDMY);
        } catch (e) {
          console.warn('No se pudo actualizar AsyncStorage con datos de usuario:', e);
        }

        // Actualizar el contexto con los nuevos datos
        const userData = {
          codUsu: codUsuResp,
          nombre: nombreResp, // Nombre, Apellido
          mail: mailResp,
          genero: generoResp,
          fechaNac: fechaNacDMY,
        };

        await updateUser(userData);
      }

      setFeedbackModal({
        visible: true,
        type: 'success',
        title: 'Éxito',
        message: 'Los datos se actualizaron correctamente'
      });
      // Informar al componente padre que los cambios se guardaron correctamente
      if (onSaveChanges) onSaveChanges();
      if (setEditMode) setEditMode(false);
    } catch (error) {
      console.error('Error al modificar usuario:', error);
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Error',
        message: 'Ocurrió un error al intentar actualizar los datos'
      });
    }
  };

  // Estilos dinámicos responsivos
  const dynamicStyles = {
    formRow: {
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      marginBottom: 15,
      width: '100%',
      maxWidth: '100%',
      flexWrap: 'wrap',
    },
    formColumn: {
      width: isMobile ? '100%' : '48%',
      maxWidth: isMobile ? '100%' : '48%',
      marginBottom: isMobile ? 10 : 0,
    },
    innerRowHalf: {
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      gap: isMobile ? 0 : 10,
    },
    innerColQuarter: {
      width: isMobile ? '100%' : '48%',
      maxWidth: isMobile ? '100%' : '48%',
      marginBottom: isMobile ? 10 : 0,
    },
  };

  return (
    <View style={{ width: '100%' }}>
      <ScrollView horizontal={false} style={{ width: '100%' }}>
        <View style={styles.formContainer}>
          <View style={dynamicStyles.formRow}>
            <View style={dynamicStyles.formColumn}>
              <Text style={styles.label}>Nombre:</Text>
              <TextInput
                style={styles.inputPill}
                placeholder="Nombre"
                value={formData.nombre}
                onChangeText={(text) => handleChange('nombre', text)}
              />
            </View>
            <View style={dynamicStyles.formColumn}>
              <Text style={styles.label}>Apellido:</Text>
              <TextInput
                style={styles.inputPill}
                placeholder="Apellido"
                value={formData.apellido}
                onChangeText={(text) => handleChange('apellido', text)}
              />
            </View>
          </View>

          <View style={dynamicStyles.formRow}>
            <View style={dynamicStyles.formColumn}>
              <Text style={styles.label}>Mail:</Text>
              <TextInput
                style={styles.inputPill}
                placeholder="Mail"
                value={formData.mail2}
                onChangeText={(text) => handleChange('mail2', text)}
                keyboardType="email-address"
              />
            </View>
            <View style={dynamicStyles.formColumn}>
              <View style={dynamicStyles.innerRowHalf}>
                <View style={dynamicStyles.innerColQuarter}>
                  <Text style={styles.label}>Género:</Text>
                  <select
                    style={styles.selectPill}
                    value={formData.sex || ''}
                    onChange={(e) => handleChange('sex', e.target.value)}
                  >
                    <option value="">Seleccionar</option>
                    <option value="M">Masculino</option>
                    <option value="F">Femenino</option>
                    <option value="O">Otro</option>
                  </select>
                </View>
                <View style={dynamicStyles.innerColQuarter}>
                  <Text style={styles.label}>Fecha de Nacimiento:</Text>
                  <TextInput
                    style={styles.inputPill}
                    placeholder="DD/MM/YYYY"
                    value={formData.fechaNac}
                    onChangeText={handleDateInput}
                    keyboardType={Platform.OS === 'web' ? 'default' : 'numeric'}
                    inputMode={Platform.OS === 'web' ? 'numeric' : undefined}
                    selectTextOnFocus={true}
                    maxLength={10}
                    onBlur={handleDateBlur}
                  />
                </View>
              </View>
            </View>
          </View>

          {/* Línea negra divisoria antes del botón */}
          {/*<View style={styles.bottomDivider} />*/}

          {/* Botón Guardar */}
          <View style={{ width: '100%', alignItems: 'flex-end' }}>
            <View style={{ width: isMobile ? '100%' : 'auto' }}>
              <TouchableOpacity
                style={[styles.saveButton, !isFormValid && styles.saveButtonDisabled]}
                onPress={handleModificar}
                disabled={!isFormValid}
              >
                <Text style={styles.saveButtonText}>Guardar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
      
      {/* Modal de feedback */}
      <FeedbackModal
        visible={feedbackModal.visible}
        type={feedbackModal.type}
        title={feedbackModal.title}
        message={feedbackModal.message}
        onClose={() => setFeedbackModal({ ...feedbackModal, visible: false })}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  formContainer: {
    padding: 5,
    maxWidth: '100%',
    flexDirection: 'column',
  },
  formField: {
    marginBottom: 15,
    width: '100%',
    maxWidth: '100%',
    flexDirection: 'column',
  },
  input: {
    flex: 1,
    height: 45,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
    paddingHorizontal: 15,
    marginHorizontal: 0,
    backgroundColor: 'white',
    fontSize: 15,
    marginBottom: 15,
    width: '100%',
  },
  label: {
    marginLeft: 12,
    marginBottom: 8,
    fontSize: 15,
    fontWeight: 'bold',
  },
  datePickerContainer: {
    marginBottom: 15,
  },
  genderPickerContainer: {
    marginBottom: 20,
  },
  pickerContainer: {
    marginBottom: 15,
  },
  picker: {
    height: 45,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
    backgroundColor: 'white',
    paddingHorizontal: 10,
  },
  saveButton: {

    backgroundColor: 'red',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 20,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      }
    ),
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
  },
  selectorContainer: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 21,
    backgroundColor: 'white',
    height: 42,
    justifyContent: 'center',
  },
  // Nuevos estilos tipo "pill" para inputs y selects
  inputPill: {
    height: 42,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 21,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    fontSize: 15,
  },
  selectPill: {
    height: 42,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 21,
    paddingLeft: 12,
    paddingRight: 28,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    fontSize: 15,
  },
  bottomDivider: {
    width: '100%',
    height: 1,
    backgroundColor: '#000000',
    marginVertical: 15,
    alignSelf: 'center',
  },
});

export default DatosUsuario;